import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock, Tag } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import StickyCTA from "@/components/site/StickyCTA";
import { useReveal } from "@/lib/reveal";
import { ARTICLES } from "@/data/articles";

export default function RessourcesPage() {
  useReveal();
  useEffect(() => {
    document.title = "Ressources & guides — Permis et étude de sol à La Réunion | Solmis Habitat";
    let meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Guides pratiques pour votre projet à La Réunion : coût du permis, étude de sol G1-G5, PLU, assainissement non collectif, extension, terrains volcaniques."
      );
    }
    window.scrollTo(0, 0);
  }, []);

  const [featured, ...rest] = ARTICLES;

  return (
    <div className="min-h-screen" data-testid="ressources-page">
      <Navbar />

      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[hsl(var(--brand-beige))]">
        <div className="container-wide">
          <div className="flex items-center gap-3 mb-6">
            <span className="rule" />
            <span className="overline">Ressources & guides</span>
          </div>
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8 reveal">
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-medium leading-[1.08] text-primary">
                Tout pour comprendre votre projet de construction{" "}
                <span className="italic font-light text-[hsl(var(--brand-terra))]">
                  à La Réunion
                </span>.
              </h1>
            </div>
            <div className="md:col-span-4 reveal">
              <p className="text-base text-muted-foreground leading-relaxed">
                Guides pratiques, explications réglementaires et conseils
                concrets pour anticiper les pièges et optimiser votre projet.
                {" "}{ARTICLES.length} articles à jour 2025.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED + LIST */}
      <section className="py-20 md:py-24">
        <div className="container-wide">
          {/* Featured */}
          <Link
            to={`/ressources/${featured.slug}`}
            data-testid={`featured-article-${featured.slug}`}
            className="reveal group block border border-border bg-white overflow-hidden hover:-translate-y-1 transition-all duration-300"
          >
            <div className="grid md:grid-cols-12">
              <div className="md:col-span-5 bg-[hsl(var(--brand-beige))] p-8 md:p-12 flex flex-col justify-between">
                <div className="flex items-center gap-3 mb-6">
                  <BookOpen
                    size={22}
                    strokeWidth={1.5}
                    className="text-[hsl(var(--brand-terra))]"
                  />
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
                    Article à la une
                  </span>
                </div>
                <div className="font-display text-6xl md:text-7xl text-primary/10 leading-none">
                  01
                </div>
              </div>
              <div className="md:col-span-7 p-8 md:p-12 flex flex-col justify-center">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border border-border rounded-full text-primary/80">
                    <Tag size={11} strokeWidth={1.8} />
                    {featured.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock size={13} strokeWidth={1.5} />
                    {featured.readingTime}
                  </span>
                </div>
                <h2 className="font-display text-2xl md:text-3xl text-primary leading-snug group-hover:text-[hsl(var(--brand-terra))] transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                  {featured.excerpt}
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Lire l'article
                  <ArrowRight
                    size={15}
                    strokeWidth={1.8}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </span>
              </div>
            </div>
          </Link>

          {/* Grid of remaining */}
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {rest.map((a, i) => (
              <Link
                key={a.slug}
                to={`/ressources/${a.slug}`}
                data-testid={`article-card-${a.slug}`}
                className="reveal group border border-border bg-white p-7 hover:-translate-y-1 transition-all duration-300"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-terra))] font-semibold">
                    {a.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Clock size={12} strokeWidth={1.5} />
                    {a.readingTime}
                  </span>
                </div>
                <h3 className="font-display text-lg md:text-xl text-primary leading-snug group-hover:text-[hsl(var(--brand-terra))] transition-colors min-h-[3.5em]">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                  {a.excerpt}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                  Lire l'article
                  <ArrowRight size={13} strokeWidth={1.8} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 md:pb-24">
        <div className="container-wide">
          <div className="reveal border border-border bg-[hsl(var(--brand-beige))] p-10 md:p-14 text-center">
            <p className="overline mb-4">Prêt à passer à l'action ?</p>
            <h2 className="font-display text-3xl md:text-4xl text-primary leading-snug max-w-3xl mx-auto">
              Demandez un devis gratuit pour votre projet de construction à La Réunion.
            </h2>
            <p className="mt-4 text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              +100 projets accompagnés · Réponse sous 24h ouvrées · Devis 100% gratuit & sans engagement
            </p>
            <div className="mt-7">
              <Link
                to="/#contact"
                data-testid="ressources-cta-devis"
                className="btn-primary group"
              >
                Demander mon devis gratuit
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
      <StickyCTA />
    </div>
  );
}
